import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';

import { useCases } from '@/features/vfo/hooks/useCases';
import { Select } from '@/features/vfo/uikit/Select';
import { BaseDto, DtoId } from '@/shared/lib/sj-orm/models/base.dto';

import { DocumentsMap } from '../types';

type Props = {
  selected: DocumentsMap;
  setSelected: (map: DocumentsMap) => void;
  showValidationErrors: boolean;
  defaultCaseId?: string;
};

export const useCaseColumn = <T extends BaseDto>({
  selected,
  setSelected,
  showValidationErrors,
  defaultCaseId,
}: Props) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'vfoImportDocument' });
  const { cases } = useCases();

  const selectOptions = useMemo(() => cases.map((c) => ({ label: c.title, value: c.id })), [cases]);

  const handleCaseChange = (id: DtoId, caseId: string | null) => {
    const next = new Map(selected);
    next.set(id, caseId);
    setSelected(next);
  };

  return useMemo(
    () => ({
      key: 'case',
      title: t('columns.case'),
      width: 190,
      ellipsis: true,
      render: (_: unknown, record: T) => {
        const hasError = selected.has(record.id) && !selected.get(record.id);

        return (
          <Select
            placeholder={t('selectCase')}
            options={selectOptions}
            style={{ width: '100%' }}
            value={selected.get(record.id) ?? defaultCaseId}
            disabled={!selected.has(record.id)}
            status={hasError && showValidationErrors ? 'error' : undefined}
            onChange={(value) => handleCaseChange(record.id, value)}
          />
        );
      },
    }),
    [selectOptions, selected, showValidationErrors],
  );
};
